<template>
  <div class="row car-gallery g-2">
    <div class="col car-gallery__main">
      <div v-if="!active" class="car-gallery__sign" style="display: flex">
        Inzerát již není aktivní
      </div>
      <div v-if="reserved" class="badge ls-1 car-card__badge">REZERVACE</div>

      <button
        type="button"
        class="car-card__favorite car-gallery__favorite"
        :class="{ active: isFavorite }"
        aria-label="Přidat do oblíbených"
        :disabled="favoriteRequestPending"
        @click.stop="onFavoriteClick"
      >
        <img :src="heartDisUrl" alt="" class="car-card__favorite-icon car-card__favorite-icon--dis" />
        <img :src="heartActUrl" alt="" class="car-card__favorite-icon car-card__favorite-icon--act" />
      </button>

      <Swiper
        class="swiper-main"
        :modules="mainModules"
        :loop="photos.length > 1"
        :threshold="20"
        :navigation="{
          prevEl: '.swiper-main .swiper-button-prev',
          nextEl: '.swiper-main .swiper-button-next',
        }"
        :thumbs="isDesktop ? { swiper: thumbsSwiper } : false"
        :keyboard="{ enabled: true }"
        :virtual="true"
        @swiper="onMainSwiper"
        @real-index-change="onMainIndexChange"
        @click="openModal"
      >
        <SwiperSlide v-for="(photo, index) in photos" :key="index" :virtual-index="index">
          <img :src="photo.main" :alt="photo.alt" loading="lazy" />
        </SwiperSlide>
        <template #container-end>
          <div class="swiper-button-prev" role="button" aria-label="Zpět">
            <i class="icon icon--xl icon-right-circle bg-white"></i>
          </div>
          <div class="swiper-button-next" role="button" aria-label="Dopředu">
            <i class="icon icon--xl icon-right-circle bg-white"></i>
          </div>
        </template>
      </Swiper>
    </div>

    <div v-if="isDesktop" class="col car-gallery__thumbs">
      <Swiper
        class="swiper-thumbs"
        :modules="thumbModules"
        :slides-per-view="4"
        direction="vertical"
        :space-between="10"
        :watch-slides-progress="true"
        :mousewheel="true"
        :loop="photos.length > 1"
        :slide-to-clicked-slide="true"
        :threshold="20"
        @swiper="onThumbsSwiper"
      >
        <SwiperSlide v-for="(photo, index) in photos" :key="index">
          <img :src="photo.thumb" :alt="photo.alt" loading="lazy" />
        </SwiperSlide>
      </Swiper>
    </div>

    <CarGalleryModal
      v-if="modalOpen"
      :photos="photos"
      :active-index="activeIndex"
      @close="closeModal"
      @update:active-index="onModalIndexChange"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, Thumbs, Keyboard, Mousewheel, Virtual } from 'swiper/modules'
import 'swiper/css'
import CarGalleryModal from './CarGalleryModal.vue'
import heartDisUrl from '~/images/heart_dis.svg'
import heartActUrl from '~/images/heart_act.svg'
import { isUserSignedIn, showFavoriteSignInModal, toggleFavorite } from '../../../js/favorite-button.js'

const props = defineProps({
  photos: { type: Array, required: true },
  reserved: { type: Boolean, default: false },
  active: { type: Boolean, default: true },
  advertisementId: { type: [String, Number], default: null },
  favorited: { type: Boolean, default: false },
})

const isFavorite = ref(props.favorited)
const favoriteRequestPending = ref(false)

function onFavoriteClick() {
  if (!isUserSignedIn()) {
    showFavoriteSignInModal()
    return
  }

  if (favoriteRequestPending.value || !props.advertisementId) return

  favoriteRequestPending.value = true
  toggleFavorite(props.advertisementId, isFavorite.value)
    .then((favorited) => {
      isFavorite.value = favorited
    })
    .catch(() => {
      // Network/server error - leave the state unchanged.
    })
    .finally(() => {
      favoriteRequestPending.value = false
    })
}

const mainModules = [Navigation, Thumbs, Keyboard, Virtual]
const thumbModules = [Mousewheel]

const activeIndex = ref(0)
const modalOpen = ref(false)
const thumbsSwiper = ref(null)
const isDesktop = ref(window.innerWidth >= 768)
let mainSwiper = null
let mediaQuery = null

onMounted(() => {
  mediaQuery = window.matchMedia('(min-width: 768px)')
  isDesktop.value = mediaQuery.matches
  mediaQuery.addEventListener('change', onBreakpointChange)
})

onUnmounted(() => {
  mediaQuery?.removeEventListener('change', onBreakpointChange)
})

function onBreakpointChange(e) {
  isDesktop.value = e.matches
}

function onMainSwiper(swiper) {
  mainSwiper = swiper
}

function onThumbsSwiper(swiper) {
  thumbsSwiper.value = swiper
}

function onMainIndexChange(swiper) {
  activeIndex.value = swiper.realIndex
}

function openModal() {
  modalOpen.value = true
}

function closeModal() {
  modalOpen.value = false
  if (mainSwiper) mainSwiper.slideToLoop(activeIndex.value, 0)
}

function onModalIndexChange(index) {
  activeIndex.value = index
}
</script>
